import React, { useEffect, useState, memo, useMemo } from 'react';

import { isEqual } from 'lodash';

import { useChildComponent } from '@gogame/hooks-ts';
import { useDocumentTitleAffix } from '@gogame/screens';

import { useCurrentUser } from '../../Auth';
import { PresetsContext } from './PresetsContext';
import PresetsPreviewButton from './PresetsPreviewButton';
import PresetsSidePage from './PresetsSidePage/PresetsSidePage';
import {
  useLoadPresets,
  useUpdatePreset,
  useRenamePreset,
  useLoadGroupedPresets,
  useSharePreset,
  useDeletePreset,
} from './data';
import { useActiveFindPreset } from './hooks/useActiveFindPreset';

export default memo(
  function ({
    data: screenData,
    activePresetId: propsActivePresetId,
    screen: presetKey,
    onApply,
    labelReset,
    labelUpdate,
    labelEmptyList,
    title,
    titleDialogAdd,
    titleDialogUpdate,
    isAddPresetWithGroup,
    isManageGroups,
    grouped = false,
    getActivePresetData,
  }) {
    const [orderBy, setOrderBy] = useState('LastUsage');
    const [childComponent, showChildComponent] = useChildComponent();

    const {
      data: ungroupedPresets,
      isLoading,
      isRefetching,
    } = useLoadPresets({
      presetKey,
      orderBy,
      enabled: !grouped,
    });

    const {
      data: groupedPresets,
      isLoading: isGroupedDataLoading,
      isRefetching: isGroupedDataRefetching,
    } = useLoadGroupedPresets({
      presetKey,
      orderBy,
      enabled: grouped,
    });
    const handleApply = ({ preset = null, applyPresetData = true }) => {
      onApply(preset, { applyPresetData });
    };
    const handleResetPreset = ({ preset = null, applyPresetData = true }) => {
      onApply(preset, { applyPresetData });
    };

    const presetsData = grouped ? groupedPresets : ungroupedPresets;
    const [updatePreset] = useUpdatePreset();
    const [renamePreset] = useRenamePreset();
    const [sharePreset] = useSharePreset();
    const [deletePreset] = useDeletePreset({ grouped, onApply: handleApply });

    const activePresetId = propsActivePresetId;
    const ownActivePreset = useActiveFindPreset(
      presetsData?.my,
      activePresetId
    );
    const publicActivePreset = useActiveFindPreset(
      presetsData?.public,
      activePresetId
    );
    const activePreset = ownActivePreset || publicActivePreset;
    const user = useCurrentUser();
    const isOwner = user?.id === activePreset?.user?.id;
    const isChanged = useMemo(() => {
      if (activePreset) {
        return !isEqual(
          JSON.stringify(activePreset.data || null),
          JSON.stringify(screenData || null)
        );
      }
      return false;
    }, [isOwner, activePreset, screenData]);
    useDocumentTitleAffix(activePreset?.title);

    useEffect(() => {
      getActivePresetData?.(activePreset);
    }, [activePreset]);

    const handleOpenPresetsList = () => {
      showChildComponent(PresetsSidePage, {
        setOrderBy,
      });
    };

    const handleRenamePreset = (preset) => {
      return renamePreset({ ...preset });
    };
    const handleUpdatePreset = (preset) => {
      return updatePreset({ ...preset, data: screenData });
    };
    const handleDeletePreset = async (preset) => {
      await deletePreset(preset);
    };

    const handleSharePreset = (preset) => {
      return sharePreset({ ...preset, isPublic: true });
    };
    const handleUnSharePreset = (preset) => {
      return sharePreset({ ...preset, isPublic: false });
    };

    return (
      <PresetsContext.Provider
        value={{
          orderBy,
          screenData,
          presetKey,
          titleDialogAdd,
          title,
          activePreset,
          isChanged,
          labelReset,
          labelUpdate,
          labelEmptyList,
          titleDialogUpdate,
          ownPresets: presetsData?.my,
          publicPresets: presetsData?.public,
          isLoading: isLoading || isGroupedDataLoading,
          isRefetching: isRefetching || isGroupedDataRefetching,
          grouped,
          isAddPresetWithGroup,
          isManageGroups,
          onUpdatePreset: handleUpdatePreset,
          onRenamePreset: handleRenamePreset,
          onDeletePreset: handleDeletePreset,
          onSharePreset: handleSharePreset,
          onUnsharePreset: handleUnSharePreset,
          onApply: handleApply,
          onResetPreset: handleResetPreset,
        }}
      >
        <PresetsPreviewButton onOpenPresetsList={handleOpenPresetsList} />
        {childComponent}
      </PresetsContext.Provider>
    );
  },
  (oldP, newP) =>
    oldP.screen === newP.screen &&
    oldP.data === newP.data &&
    oldP.activePresetId === newP.activePresetId
);
